import * as React from "react";
import { ICommunicationNote } from "intake_shared/src/types";
import { AclContext } from "../../../../../contexts/AclContext";
import { Button } from "../../../bits/Button/Button";
import { Dialog } from "../../../bits/Dialog/Dialog";
import { AlertDialog } from "../../../bits/AlertDialog/AlertDialog";
import { TextArea } from "../../../bits/TextArea/TextArea";
import EditIcon from "../../../icons/Edit";
import TrashIcon from "../../../icons/Trash";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import EyeIcon from "../../../icons/Eye";
import PlusCircleIcon from "../../../icons/PlusCircle";
import styles from "./CommunicationNoteDialog.module.scss";

export interface CommunicationNoteActions {
    onRefresh: () => Promise<boolean>;
    onCreate: (
        channel: ICommunicationNote["channel"],
        recordId: string,
        body: string
    ) => Promise<boolean>;
    onUpdate: (noteId: string, body: string) => Promise<boolean>;
    onDelete: (noteId: string) => Promise<boolean>;
}

export interface CommunicationNotesHistoryProps {
    notes?: ICommunicationNote[];
    noteActions?: CommunicationNoteActions;
}

interface CommunicationNoteDialogProps {
    channel: ICommunicationNote["channel"];
    recordId: string;
    notes: ICommunicationNote[];
    actions: CommunicationNoteActions;
}

export function canManageCommunicationNote(
    note: ICommunicationNote,
    email: string | null | undefined,
    isSuperAdmin: boolean
): boolean {
    return (
        isSuperAdmin ||
        Boolean(email && note.author.email.toLowerCase() === email.toLowerCase())
    );
}

export default function CommunicationNoteDialog({
    channel,
    recordId,
    notes,
    actions,
}: CommunicationNoteDialogProps) {
    const acl = React.useContext(AclContext);
    const [isOpen, setIsOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [draft, setDraft] = React.useState("");
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [editDraft, setEditDraft] = React.useState("");
    const [deletingId, setDeletingId] = React.useState<string | null>(null);
    const [refreshFailed, setRefreshFailed] = React.useState(false);
    const communicationType = channel === "call" ? "Call" : "SMS";
    const sortedNotes = [...notes].sort(
        (left, right) =>
            Date.parse(right.created_at) - Date.parse(left.created_at) ||
            right.note_id.localeCompare(left.note_id)
    );

    const openDialog = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setIsOpen(true);
        setIsLoading(true);
        setRefreshFailed(false);
        try {
            setRefreshFailed(!(await actions.onRefresh()));
        } finally {
            setIsLoading(false);
        }
    };

    const closeDialog = () => {
        if (isSaving || deletingId) return;
        setDraft("");
        setEditingId(null);
        setEditDraft("");
        setIsOpen(false);
    };

    const addNote = async () => {
        const body = draft.trim();
        if (!body || isSaving) return;
        setIsSaving(true);
        try {
            if (await actions.onCreate(channel, recordId, body)) {
                setDraft("");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const saveEdit = async () => {
        if (!editingId || !editDraft.trim() || isSaving) return;
        setIsSaving(true);
        try {
            if (await actions.onUpdate(editingId, editDraft.trim())) {
                setEditingId(null);
                setEditDraft("");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const deleteNote = async () => {
        if (!deletingId || isSaving) return;
        setIsSaving(true);
        try {
            if (await actions.onDelete(deletingId)) {
                if (editingId === deletingId) {
                    setEditingId(null);
                    setEditDraft("");
                }
                setDeletingId(null);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div
            className={styles.noteCell}
            onClick={(event) => event.stopPropagation()}
        >
            <Button
                variant="normal"
                size="auto"
                className={styles.noteButton}
                iconLeft={notes.length ? <EyeIcon /> : <PlusCircleIcon />}
                onClick={openDialog}
                aria-label={
                    notes.length
                        ? `View ${notes.length} ${communicationType} notes`
                        : `Add ${communicationType} note`
                }
            >
                {notes.length
                    ? `${notes.length} ${notes.length === 1 ? "note" : "notes"}`
                    : "Add note"}
            </Button>
            <Dialog
                open={isOpen}
                onClose={closeDialog}
                onCancel={closeDialog}
                onSave={addNote}
                saveText={isSaving ? "Saving..." : "Add note"}
                title={`${communicationType} notes`}
                isSubmitBtnDisabled={
                    isSaving ||
                    isLoading ||
                    refreshFailed ||
                    Boolean(editingId) ||
                    !draft.trim()
                }
                isCancelBtnDisabled={isSaving || Boolean(deletingId)}
            >
                <div className={styles.content}>
                    <p className={styles.description}>
                        Notes added in Intake or Mantle appear together here.
                    </p>
                    {isLoading ? (
                        <p className={styles.description} role="status">
                            Refreshing notes...
                        </p>
                    ) : null}
                    {refreshFailed ? (
                        <p className={styles.error} role="alert">
                            Notes could not be refreshed. Close and reopen this
                            dialog to try again.
                        </p>
                    ) : null}
                    {sortedNotes.length ? (
                        <div className={styles.noteList}>
                            {sortedNotes.map((note) => (
                                <article
                                    className={styles.noteCard}
                                    key={note.note_id}
                                >
                                    <div className={styles.noteMeta}>
                                        <span className={styles.author}>
                                            {note.author.name}
                                        </span>
                                        <span className={styles.source}>
                                            {note.origin_system === "mantle"
                                                ? "Mantle"
                                                : "Intake"}
                                        </span>
                                        <time dateTime={note.created_at}>
                                            {new Intl.DateTimeFormat("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                                hour: "numeric",
                                                minute: "2-digit",
                                            }).format(new Date(note.created_at))}
                                        </time>
                                        {note.updated_by ? (
                                            <span className={styles.description}>
                                                edited
                                            </span>
                                        ) : null}
                                    </div>
                                    {editingId === note.note_id ? (
                                        <>
                                            <TextArea
                                                label="Edit note"
                                                name={`${channel}-${recordId}-edit-note`}
                                                value={editDraft}
                                                onChange={(event) =>
                                                    setEditDraft(event.target.value)
                                                }
                                                maxLength={1000}
                                                disabled={isSaving}
                                            />
                                            <div className={styles.actions}>
                                                <Button
                                                    onClick={saveEdit}
                                                    disabled={
                                                        isSaving || !editDraft.trim()
                                                    }
                                                >
                                                    Save changes
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    onClick={() =>
                                                        setEditingId(null)
                                                    }
                                                    disabled={isSaving}
                                                >
                                                    Cancel edit
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <p className={styles.noteBody}>
                                            {note.body}
                                        </p>
                                    )}
                                    {canManageCommunicationNote(
                                        note,
                                        acl?.authUser.user?.email,
                                        acl?.userAcl.acl?.super_admin === true
                                    ) && editingId !== note.note_id ? (
                                        <div className={styles.actions}>
                                            <Button
                                                mode="outline"
                                                iconLeft={<EditIcon />}
                                                disabled={
                                                    isSaving ||
                                                    isLoading ||
                                                    refreshFailed
                                                }
                                                onClick={() => {
                                                    setEditingId(note.note_id);
                                                    setEditDraft(note.body);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="danger"
                                                iconLeft={
                                                    <TrashIcon stroke="var(--color-white)" />
                                                }
                                                disabled={
                                                    isSaving ||
                                                    isLoading ||
                                                    refreshFailed
                                                }
                                                onClick={() =>
                                                    setDeletingId(note.note_id)
                                                }
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    ) : null}
                                </article>
                            ))}
                        </div>
                    ) : (
                        <EmptyListState
                            wrapperClass={styles.emptyNotes}
                            emptyMessage="No notes have been added to this communication."
                            stateHelperText="Add context for the next person reviewing the chart."
                        />
                    )}
                    <TextArea
                        label="Add a note"
                        name={`${channel}-${recordId}-add-note`}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        placeholder="Add context about this communication..."
                        maxLength={1000}
                        disabled={isSaving}
                    />
                </div>
            </Dialog>
            <AlertDialog
                open={Boolean(deletingId)}
                onClose={() => !isSaving && setDeletingId(null)}
                onCancelClick={() => setDeletingId(null)}
                onSaveClick={deleteNote}
                title="Delete note?"
                description="This note will be removed from Intake and Mantle."
                saveText={isSaving ? "Deleting..." : "Delete"}
                isSaveBtnDisabled={isSaving}
                isCancelBtnDisabled={isSaving}
                isDeleting
            />
        </div>
    );
}
